import { useState } from 'react';
import { useConfirm } from './useConfirm';

export interface EditableEntityToggleOpts<T> {
  /** The row being edited (null in create mode). */
  entity: T | null;
  isCreate: boolean;
  /** Confirm-dialog texts shown before DEACTIVATING (activate needs no confirm). */
  confirmTitle: string;
  confirmMessage: string;
  confirmLabel: string;
  /** Reactivate — typically `updateX(id, { is_active: true })`. Any resolved
   *  value is ignored, so the API helper's return type doesn't have to match. */
  activate: (id: number) => Promise<unknown>;
  /** Deactivate — typically the soft-delete `deleteX(id)`. Resolved value ignored. */
  deactivate: (id: number) => Promise<unknown>;
  onSaved: () => void;
  /** Set (string) or clear (null) the modal's error banner. */
  onError: (msg: string | null) => void;
}

/**
 * Active/inactive toggle for an admin catalog edit modal. Applies immediately
 * (its own API call), confirms before deactivating, tracks `isActive` + the
 * busy flag, and refreshes via `onSaved`. Replaces the inline `handleToggleActive`
 * that was duplicated across the four admin `*EditModal` components.
 *
 * The modal seeds the initial state from the row via the returned `setIsActive`
 * (in its load effect), and renders the menu item with `buildCatalogMenu`.
 */
export function useEditableEntityToggle<T extends { id: number }>(opts: EditableEntityToggleOpts<T>) {
  const confirm = useConfirm();
  const [isActive, setIsActive] = useState(true);
  const [toggling, setToggling] = useState(false);

  const toggle = async () => {
    if (opts.isCreate || !opts.entity) return;
    const next = !isActive;
    if (!next) {
      const ok = await confirm({
        title: opts.confirmTitle,
        message: opts.confirmMessage,
        confirmLabel: opts.confirmLabel,
      });
      if (!ok) return;
    }
    setToggling(true);
    opts.onError(null); // clear any stale form-validation error before applying
    try {
      if (next) await opts.activate(opts.entity.id);
      else await opts.deactivate(opts.entity.id);
      setIsActive(next);
      opts.onSaved();
    } catch (e: unknown) {
      opts.onError(e instanceof Error ? e.message : 'Error cambiando el estado');
    } finally {
      setToggling(false);
    }
  };

  return { isActive, setIsActive, toggling, toggle };
}
